import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import { Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import {
  type AiSummaryFieldConfig,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  isDatabaseTextValue
} from '@databases/types/database';
import React, { type FunctionComponent, type ReactElement } from 'react';
import { useIntl } from 'react-intl-next';

import { formatRelativeTime } from '@canvas/intl/format-relative-time';

import {
  aiFieldCellRequestCoordinator,
  useAiFieldCellRequestState
} from '../cell-request-coordinator';

import { runAiSummaryField } from './run-ai-summary-field';
import { useIsAiSummaryFieldEnabled } from './use-is-ai-summary-field-enabled';

const buttonStyles = xcss({
  width: '24px',
  height: '24px',
  padding: 'space.0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'box-shadow 0.2s, background-color 0.2s',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered',
    boxShadow: 'elevation.shadow.overlay'
  }
});

export interface UseAiSummaryCellActionOptions {
  contentId: string;
  entryId: string;
  field: DatabaseField;
  isVisible: boolean;
  value: DatabaseValue;
}

export type UseAiSummaryCellActionResult =
  | {
      isAiSummaryFieldEnabled: false;
      cellAction: undefined;
    }
  | {
      isAiSummaryFieldEnabled: true;
      cellAction: ReactElement | undefined;
    };

const AI_SUMMARY_DISABLED_RESULT: UseAiSummaryCellActionResult = {
  isAiSummaryFieldEnabled: false,
  cellAction: undefined
};
const AI_SUMMARY_ENABLED_WITHOUT_ACTION_RESULT: UseAiSummaryCellActionResult = {
  isAiSummaryFieldEnabled: true,
  cellAction: undefined
};

interface AiSummaryCellActionProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  targetFieldName: string;
  config: AiSummaryFieldConfig;
  value: DatabaseValue;
}

const AiSummaryCellAction: FunctionComponent<AiSummaryCellActionProps> = ({
  contentId,
  entryId,
  fieldId,
  targetFieldName,
  config,
  value
}) => {
  const intl = useIntl();
  const target = {
    contentId,
    aiFieldType: 'summary' as const,
    fieldId,
    entryId
  };
  const requestState = useAiFieldCellRequestState(target);
  const lastUpdatedAt =
    value.type === DatabaseFieldType.AI_SUMMARY
      ? value.value.metadata?.lastUpdatedAt
      : undefined;
  const valueState =
    isDatabaseTextValue(value.value) && value.value.plain.trim().length > 0
      ? 'existing'
      : 'empty';
  const handleClick = () => {
    const result = aiFieldCellRequestCoordinator.enqueue({
      target,
      run: () =>
        runAiSummaryField({
          contentId,
          fieldId,
          targetFieldName,
          config,
          locale: intl.locale,
          operation: 'single-entry-generate',
          entryId
        })
    });

    if (result.kind === 'enqueued') {
      void result.completion.catch(() => undefined);
    }
  };

  const actionTooltip = intl.formatMessage(
    valueState === 'empty'
      ? messages['field-actions.ai-summary-cell-generate']
      : messages['field-actions.ai-summary-cell-regenerate']
  );
  const relativeTime = formatRelativeTime(lastUpdatedAt, intl);
  const tooltipContent = relativeTime
    ? intl.formatMessage(messages['field-actions.ai-summary-cell-last-updated'], {
        action: actionTooltip,
        relativeTime
      })
    : actionTooltip;

  return (
    <Tooltip position="top" content={tooltipContent}>
      <Pressable
        aria-label={actionTooltip}
        isDisabled={requestState.status !== 'idle'}
        onClick={(event) => {
          event.stopPropagation();
          handleClick();
        }}
        xcss={buttonStyles}
        testId="cell-ai-summary-generate-button"
      >
        <AiSparkleIcon label="" size="small" color={token('color.icon')} />
      </Pressable>
    </Tooltip>
  );
};

export const useAiSummaryCellAction = ({
  contentId,
  entryId,
  field,
  isVisible,
  value
}: UseAiSummaryCellActionOptions): UseAiSummaryCellActionResult => {
  const isAiSummaryFieldEnabled = useIsAiSummaryFieldEnabled();

  if (!isAiSummaryFieldEnabled) {
    return AI_SUMMARY_DISABLED_RESULT;
  }

  if (!isVisible || field.type !== DatabaseFieldType.AI_SUMMARY) {
    return AI_SUMMARY_ENABLED_WITHOUT_ACTION_RESULT;
  }

  const config =
    field.settings.aiFieldConfig?.aiFieldType === 'summary'
      ? field.settings.aiFieldConfig.field
      : undefined;

  if (!config) {
    return AI_SUMMARY_ENABLED_WITHOUT_ACTION_RESULT;
  }

  return {
    isAiSummaryFieldEnabled: true,
    cellAction: (
      <AiSummaryCellAction
        contentId={contentId}
        entryId={entryId}
        fieldId={field.id}
        targetFieldName={field.name}
        config={config}
        value={value}
      />
    )
  };
};
